import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { Subject, takeUntil } from 'rxjs';
import { NgxMediaAnalyticsService } from '@go-modules/services/media/analytics/media-analytics.service';
import { RouterService } from '@go-modules/services/router/router.service';
import { MediaDisplayMode } from 'go-modules/feedback-session/feedback-session.provider';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

interface Bindings {
	mainController: any;
	singleMediaDisplayController: any;
	mediaDisplayController: any;
	media: any;
}

export class PlaybackViewController implements Bindings {
	public mainController: any;
	public singleMediaDisplayController: any;
	public mediaDisplayController: any;
	public media: any;

	public currentMedia: any = null;
	public playerId: string;
	public mediaPreviewOptions: any;
	public isPostDiscardMode: boolean;
	public isLoading: boolean;
	public isTrimModeActivated = false;
	public recentlyFinishedRecording = false;
	public environmentVarsService: EnvironmentVarsService;

	private destroyed$ = new Subject<void>();
	private processingAnnouncementKey: string = null;

	/* @ngInject */
	constructor (
		private feedbackSession,
		private $analytics,
		private eventService: EventService,
		private featureFlag: NgxFeatureFlagService,
		private ngxRouterService: RouterService,
		public ngxMediaAnalyticsService: NgxMediaAnalyticsService,
		private taskService: NgxTaskService,
		private $mdLiveAnnouncer,
		private translateFilter: ng.translate.ITranslateFilter
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	/**
	 * Handles controller init lifecycle hook
	 */
	public $onInit (): void {
		this.currentMedia = this.media;
		this.playerId = this.feedbackSession.sessionPlayerId(this.mainController.session);
		this.isPostDiscardMode = this.checkIsPostDiscardMode();
		this.mediaPreviewOptions = this.getMediaPreviewOptions(this.isPostDiscardMode);
		this.mediaPreviewOptions.isVideoSeekingDisabled = this.mainController.activity.is_video_seeking_disabled;
		this.mediaPreviewOptions.isClosedCaptionsDisabled = this.mainController.activity.is_source_media_cc_disabled;
		this.mediaPreviewOptions.disableControlsForSingleRecording = this.disableControlsForSingleRecording();
		this.mediaPreviewOptions.isCommentOnlySingleAttempt = this.mainController.activity.isCommentOnlySingleAttempt();
		// Attach some meta data for analytics
		this.mediaPreviewOptions.sessionId = this.mainController.session.session_id;
		this.mediaPreviewOptions.activityId = this.mainController.activity.activity_id;
		// if viewMode record still in url
		const viewModeParam = this.ngxRouterService.getCurrentQueryParams().viewMode;
		this.recentlyFinishedRecording = viewModeParam === MediaDisplayMode.RECORD;
		this.announceProcessingMessage();

		this.eventService.listen([
			EVENT_NAMES.FEEDBACK_SESSION_TRIM_ACTIVATE,
			EVENT_NAMES.FEEDBACK_SESSION_TRIM_DEACTIVATE
		])
			.pipe(takeUntil(this.destroyed$))
			.subscribe((event: GoEvent) => {
				if (event.name === EVENT_NAMES.FEEDBACK_SESSION_TRIM_ACTIVATE) {
					this.isTrimModeActivated = true;
				} else if (event.name === EVENT_NAMES.FEEDBACK_SESSION_TRIM_DEACTIVATE) {
					this.isTrimModeActivated = false;
				}
			});
	}

	/**
	 * Handles controller bindings change lifecycle hook
	 */
	public $onChanges (changes: any): void {
		if (changes.media) {
			this.mediaChangeHandler(changes.media.currentValue);
		}
	}

	public $doCheck (): void {
		this.announceProcessingMessage();
	}

	public $onDestroy (): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}

	public hasSubmitTask (): boolean {
		return this.shouldShowTasks() && this.taskService.hasSubmitTask(this.mainController.activity.activity_id);
	}

	/**
	 * Whether post/discard controls should be displayed
	 */
	public shouldDisplayPostDiscardControls (): boolean {
		if (this.shouldShowTasks() && !this.recentlyFinishedRecording) {
			return false;
		}
		return this.isPostDiscardMode
			&& !!this.currentMedia
			&& this.currentMedia.isReady()
			&& !this.mediaDisplayController.wasRecordingStoppedBySomeoneElse();
	}

	public getDiscardedMessage (): string {
		if (!this.mainController.session.archived_by_user) {
			return 'big-blue-button_recording-discarded';
		}

		return 'big-blue-button_recording-discarded-by-user';
	}

	public getPostedMessage (): string {
		if (!this.mainController.session.posted_by_user) {
			return 'big-blue-button_recording-posted';
		}

		return 'big-blue-button_recording-posted-by-user';
	}

	public shouldShowTasks (): boolean {
		const activity = this.mainController.activity;
		return activity.show_tasks === true || this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}

	/**
	 * Set trim params before posting
	 */
	public postSession (): void {
		if (this.isSubmitDisabled()) {
			return;
		}

		// Conditionally include trim params
		const trimParams = this.mediaPreviewOptions.trimPoints;
		if (trimParams !== undefined && trimParams.start != null && trimParams.end != null) {
			this.mainController.session.trim = trimParams;

			// reset the media analytics because trimming was causing the numbers to be wrong
			const instanceId = this.mainController.media.media_id + '-' + this.mainController.user.user_id;
			this.ngxMediaAnalyticsService.removeInstance(instanceId);

			this.$analytics.eventTrack('trim-video', {
				category: 'session'
			});
		}

		this.mainController.postSession().then(() => {
			this.mainController.exit();
		});
	}

	public isSubmitDisabled (): boolean {
		return this.isLoading === true
			|| !!this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY);
	}

	public discardSessionConfirm () {
		this.mainController.discardSessionConfirm().then(() => {
			this.mainController.exit();
		});
	}

	public wasRecordingStoppedByMe (): boolean {
		return this.mediaDisplayController.wasRecordingStoppedByMe();
	}

	public shouldAdjustLayout (): boolean {
		const containerWidth = document.querySelector<HTMLElement>('.media-processing-message')?.offsetWidth;
		if (containerWidth) {
			return containerWidth < 480;
		}
		return false;
	}

	public shouldShowLinkButtonAsLabel (): boolean {
		const containerWidth = document.querySelector<HTMLElement>('.media-container')?.offsetWidth;
		if (containerWidth) {
			return containerWidth > 480;
		}
		return false;
	}

	public canRestart (): boolean {
		if (this.media?.isYoutube() || this.media?.isUpload()) {
			return false;
		}
		return !this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)
			&& this.mainController.activity.allowsPractice();
	}

	public restartSession () {
		this.isLoading = true;
		this.mainController.restartSessionConfirm()
			.finally(() => this.isLoading = false);
	}

	public deactivateTrimMode () {
		this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_TRIM_DEACTIVATE);
	}

	/**
	 * Check if media is comment only
	 */
	public disableControlsForSingleRecording (): boolean {
		return this.mainController.activity.isCommentOnlySingleAttempt()
			&& this.mainController.session.isPresenter(this.mainController.user);
	}

	private getMediaPreviewOptions (isPostDiscardMode: boolean): any {
		const options: any = { showTrim: this.mediaDisplayController.wasRecordingStoppedByMe() };
		// Auto play for single videos where autoplay wasnt disabled
		options.autoPlay = !!this.singleMediaDisplayController && this.mediaDisplayController.shouldAutoPlay();
		options.hideControls = !this.singleMediaDisplayController; // Hide controls for multi videos
		if (isPostDiscardMode) {
			options.trimPoints = {
				start: null,
				end: null
			};
		} else {
			options.toggleCommentCaptionsCallback = this.feedbackSession.toggleCommentCaptions;
		}

		return options;
	}

	/**
	 * Whether or not it is post / discard mode
	 */
	private checkIsPostDiscardMode (): boolean {
		const mayPost = !this.mainController.session.isPosted() &&
			this.mainController.session.isOwner(this.mainController.user);
		return mayPost &&
			this.feedbackSession.options.manualPostDiscard &&
			this.singleMediaDisplayController;
	}

	/**
	 * Handles media changes
	 */
	private mediaChangeHandler (media: any): void {
		if (!media) {
			return;
		}

		this.currentMedia = media;
		this.announceProcessingMessage();
	}

	private announceProcessingMessage (): void {
		const isPending = typeof this.media?.isPending === 'function' && this.media.isPending();

		if (!isPending) {
			this.processingAnnouncementKey = null;
			return;
		}

		const announcementKey = this.feedbackSession.lastMediaDisplayMode === MediaDisplayMode.RECORD
			? 'media-preview_still-processing-message'
			: 'common_processing_media';

		if (this.processingAnnouncementKey === announcementKey) {
			return;
		}

		this.processingAnnouncementKey = announcementKey;
		this.$mdLiveAnnouncer.announce(this.translateFilter(announcementKey));
	}
}
